import React, { useState } from "react";
import { Pencil, Check, X, AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";

interface RegistrationGoalProgressProps {
  verifiedTickets: number;
  flaggedTickets: number;
  goal: number;
  eventId: string;
  onGoalUpdate?: (newGoal: number) => void;
  editable?: boolean;
}

const RegistrationGoalProgress: React.FC<RegistrationGoalProgressProps> = ({
  verifiedTickets,
  flaggedTickets,
  goal,
  eventId,
  onGoalUpdate,
  editable = false,
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState(goal.toString());

  const totalTickets = verifiedTickets + flaggedTickets;
  const verifiedPercent = goal > 0 ? Math.min((verifiedTickets / goal) * 100, 100) : 0;
  const flaggedPercent = goal > 0 ? Math.min((flaggedTickets / goal) * 100, 100 - verifiedPercent) : 0;
  const totalPercent = Math.round((verifiedTickets / goal) * 100);

  const handleSave = () => {
    const newGoal = parseInt(editValue, 10);
    if (!isNaN(newGoal) && newGoal > 0) {
      onGoalUpdate?.(newGoal);
      setIsEditing(false);
    }
  };

  const handleCancel = () => {
    setEditValue(goal.toString());
    setIsEditing(false);
  };

  const getProgressColor = () => {
    if (totalPercent >= 80) return "bg-emerald-500";
    if (totalPercent >= 50) return "bg-amber-500";
    return "bg-primary";
  };

  return (
    <div className="bg-card border border-border rounded-lg p-4 space-y-3">
      <div className="flex items-center justify-between">
        <h3 className="font-semibold text-foreground">Registration Goal</h3>
        {editable && !isEditing && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setIsEditing(true)}
            className="h-8 w-8 p-0"
          >
            <Pencil className="h-4 w-4" />
          </Button>
        )}
        {isEditing && (
          <div className="flex items-center gap-2">
            <Input
              type="number"
              value={editValue}
              onChange={(e) => setEditValue(e.target.value)}
              className="w-24 h-8"
              min={1}
            />
            <Button
              variant="ghost"
              size="sm"
              onClick={handleSave}
              className="h-8 w-8 p-0 text-emerald-600 hover:text-emerald-700"
            >
              <Check className="h-4 w-4" />
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={handleCancel}
              className="h-8 w-8 p-0 text-destructive hover:text-destructive"
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
        )}
      </div>

      {/* Progress bar */}
      <div className="space-y-2">
        <div className="flex items-baseline justify-between">
          <div className="flex items-baseline gap-2">
            <span className="text-3xl font-bold text-foreground">{verifiedTickets}</span>
            <span className="text-muted-foreground">/ {goal}</span>
          </div>
          <span className={cn(
            "text-sm font-medium",
            totalPercent >= 80 ? "text-emerald-600" : 
            totalPercent >= 50 ? "text-amber-600" : "text-muted-foreground"
          )}>
            {totalPercent}%
          </span>
        </div>

        <div className="h-3 bg-muted rounded-full overflow-hidden flex">
          <div
            className={cn("h-full transition-all duration-500", getProgressColor())}
            style={{ width: `${verifiedPercent}%` }}
          />
          {flaggedTickets > 0 && (
            <div
              className="h-full bg-amber-400/60 transition-all duration-500"
              style={{ width: `${flaggedPercent}%` }}
            />
          )}
        </div>

        <div className="flex items-center justify-between text-sm">
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-1.5">
              <div className={cn("w-2.5 h-2.5 rounded-full", getProgressColor())} />
              <span className="text-muted-foreground">Verified ({verifiedTickets})</span>
            </div>
            {flaggedTickets > 0 && (
              <div className="flex items-center gap-1.5">
                <div className="w-2.5 h-2.5 rounded-full bg-amber-400/60" />
                <span className="text-muted-foreground">Needs Review ({flaggedTickets})</span>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Warning callout for flagged tickets */}
      {flaggedTickets > 0 && (
        <div className="flex items-start gap-2 p-2 bg-amber-500/10 border border-amber-500/20 rounded-md">
          <AlertTriangle className="h-4 w-4 text-amber-600 mt-0.5 flex-shrink-0" />
          <p className="text-sm text-amber-700 dark:text-amber-400">
            {flaggedTickets} ticket{flaggedTickets !== 1 ? "s" : ""} need review and are not counted toward the goal
          </p>
        </div>
      )}
    </div>
  );
};

export default RegistrationGoalProgress;
